import React, { useEffect, useMemo, useState } from 'react';
import { X, Sparkles, Users, DollarSign, Clock, MapPin, Check, AlertCircle, Edit2 } from 'lucide-react';
import { buildAutoGroupSuggestions } from '../utils/autoGroupJobs';
import { formatJobTime } from '../utils/jobData';
import './AutoGroupModal.css';

// Auto-group AI suggestion modal. Computes route groupings from unassigned
// jobs and offers per-route overrides before applying.
function AutoGroupModal({
  isOpen,
  onClose,
  selectedDate,
  unassignedJobs,
  allJobsForDate,
  employees,
  timeOffRequests = [],
  onApply,
}) {
  const [targetPerPerson, setTargetPerPerson] = useState(300);
  const [overrides, setOverrides] = useState({}); // routeIndex -> { teamIds, skipped, droppedJobIds }
  const [applying, setApplying] = useState(false);

  // Build the unavailable-employee list from approved/pending time-off requests
  const unavailableIds = useMemo(() => {
    const ids = new Set();
    (timeOffRequests || []).forEach(req => {
      if ((req.status === 'approved' || req.status === 'pending') &&
          req.startDate <= selectedDate && req.endDate >= selectedDate) {
        ids.add(req.employeeId);
      }
    });
    return Array.from(ids);
  }, [timeOffRequests, selectedDate]);

  const suggestions = useMemo(() => {
    if (!isOpen) return [];
    return buildAutoGroupSuggestions({
      unassignedJobs,
      allJobsForDate,
      employees,
      unavailableEmployeeIds: unavailableIds,
      targetPerPerson,
    });
  }, [isOpen, unassignedJobs, allJobsForDate, employees, unavailableIds, targetPerPerson]);

  // Reset overrides when suggestions regenerate (target changed, modal reopened)
  useEffect(() => {
    if (!isOpen) return;
    const initial = {};
    suggestions.forEach((s, i) => {
      initial[i] = { teamIds: s.teamIds, skipped: false, droppedJobIds: new Set() };
    });
    setOverrides(initial);
  }, [isOpen, suggestions]);

  if (!isOpen) return null;

  const activeEmployees = employees.filter(e => (e.status === 'active' || e.status === 'onboarding') && !unavailableIds.includes(e.id));

  const setOverride = (idx, patch) => {
    setOverrides(prev => ({ ...prev, [idx]: { ...prev[idx], ...patch } }));
  };

  const toggleEmployee = (idx, empId) => {
    setOverrides(prev => {
      const current = prev[idx] || { teamIds: [], skipped: false, droppedJobIds: new Set() };
      const teamIds = current.teamIds.includes(empId)
        ? current.teamIds.filter(id => id !== empId)
        : [...current.teamIds, empId];
      return { ...prev, [idx]: { ...current, teamIds } };
    });
  };

  const toggleJob = (idx, jobId) => {
    setOverrides(prev => {
      const current = prev[idx] || { teamIds: [], skipped: false, droppedJobIds: new Set() };
      const dropped = new Set(current.droppedJobIds);
      if (dropped.has(jobId)) dropped.delete(jobId); else dropped.add(jobId);
      return { ...prev, [idx]: { ...current, droppedJobIds: dropped } };
    });
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      // Build the final list of route assignments to apply
      const final = suggestions
        .map((s, idx) => {
          const ov = overrides[idx];
          if (!ov || ov.skipped) return null;
          const jobs = s.jobs.filter(j => !ov.droppedJobIds.has(j.id));
          if (jobs.length === 0 || ov.teamIds.length === 0) return null;

          const teamObjs = activeEmployees.filter(e => ov.teamIds.includes(e.id));
          const teamNames = teamObjs.map(e => `${e.firstName} ${e.lastName?.[0] || ''}.`);
          const sorted = [...ov.teamIds].sort();
          return {
            jobs,
            teamIds: ov.teamIds,
            teamNames,
            routeId: `team_${sorted.join('_')}`,
            routeName: teamNames.map(n => n.split(' ')[0]).filter(Boolean).join(' & ') || null,
          };
        })
        .filter(Boolean);
      await onApply(final);
      onClose();
    } finally {
      setApplying(false);
    }
  };

  const totalRoutes = Object.values(overrides).filter(o => !o.skipped).length;
  const totalJobs = suggestions.reduce((sum, s, idx) => {
    const ov = overrides[idx];
    if (!ov || ov.skipped) return sum;
    return sum + s.jobs.filter(j => !ov.droppedJobIds.has(j.id)).length;
  }, 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content auto-group-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="auto-group-title">
            <Sparkles size={22} />
            <div>
              <h2>Auto-Group Jobs</h2>
              <p>Suggested route groupings based on location, time, and a ${targetPerPerson}/person target</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="auto-group-controls">
          <label>
            Target per person/day:
            <span className="target-input">
              <DollarSign size={14} />
              <input
                type="number"
                min="100"
                step="25"
                value={targetPerPerson}
                onChange={(e) => setTargetPerPerson(Math.max(50, parseInt(e.target.value, 10) || 300))}
              />
            </span>
          </label>
          <div className="auto-group-summary">
            <span><strong>{totalRoutes}</strong> route{totalRoutes !== 1 ? 's' : ''}</span>
            <span><strong>{totalJobs}</strong> job{totalJobs !== 1 ? 's' : ''}</span>
            <span>{unassignedJobs.length} unassigned input</span>
          </div>
        </div>

        <div className="auto-group-body">
          {suggestions.length === 0 ? (
            <div className="auto-group-empty">
              <AlertCircle size={36} />
              <p>No unassigned jobs to group on {selectedDate}.</p>
            </div>
          ) : (
            suggestions.map((s, idx) => {
              const ov = overrides[idx] || { teamIds: [], skipped: false, droppedJobIds: new Set() };
              const activeJobs = s.jobs.filter(j => !ov.droppedJobIds.has(j.id));
              const revenue = activeJobs.reduce((sum, j) => sum + (j.price || 0), 0);
              const perPerson = ov.teamIds.length > 0 ? revenue / ov.teamIds.length : 0;
              const minutes = s.estimatedMinutes;
              const hours = Math.round((minutes / 60) * 10) / 10;

              const teamNames = activeEmployees
                .filter(e => ov.teamIds.includes(e.id))
                .map(e => `${e.firstName} ${e.lastName?.[0] || ''}.`);

              return (
                <div key={idx} className={`auto-group-route ${ov.skipped ? 'skipped' : ''}`}>
                  <div className="route-summary">
                    <div className="route-header-line">
                      <span className="route-label">Route {idx + 1}</span>
                      <span className="route-team-names">
                        <Users size={14} /> {teamNames.length > 0 ? teamNames.join(' & ') : <em>No team</em>}
                      </span>
                    </div>
                    <div className="route-metrics">
                      <span className={perPerson >= targetPerPerson * 0.85 ? 'good' : perPerson > 0 ? 'low' : ''}>
                        <DollarSign size={14} /> ${revenue.toFixed(0)} total
                        {ov.teamIds.length > 0 && <em> (${perPerson.toFixed(0)}/person)</em>}
                      </span>
                      <span><Clock size={14} /> ~{hours}h</span>
                      <span><MapPin size={14} /> {activeJobs.length} job{activeJobs.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="route-actions">
                      <label className="skip-label">
                        <input
                          type="checkbox"
                          checked={ov.skipped}
                          onChange={(e) => setOverride(idx, { skipped: e.target.checked })}
                        />
                        Skip this route
                      </label>
                    </div>
                  </div>

                  {!ov.skipped && (
                    <>
                      <div className="route-section">
                        <div className="route-section-title"><Edit2 size={13} /> Team (click to change)</div>
                        <div className="employee-chips">
                          {activeEmployees.map(emp => {
                            const isOn = ov.teamIds.includes(emp.id);
                            return (
                              <button
                                key={emp.id}
                                className={`employee-chip ${isOn ? 'selected' : ''}`}
                                onClick={() => toggleEmployee(idx, emp.id)}
                              >
                                {isOn && <Check size={12} />}
                                {emp.firstName} {emp.lastName?.[0]}.
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="route-section">
                        <div className="route-section-title">Jobs in this route</div>
                        <div className="route-jobs">
                          {s.jobs.map(job => {
                            const dropped = ov.droppedJobIds.has(job.id);
                            return (
                              <div key={job.id} className={`route-job ${dropped ? 'dropped' : ''}`}>
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={!dropped}
                                    onChange={() => toggleJob(idx, job.id)}
                                  />
                                  <span className="route-job-time">{formatJobTime(job.scheduledTime) || 'No time'}</span>
                                  <span className="route-job-client">{job.clientName}</span>
                                  <span className="route-job-address">{job.address}</span>
                                  <span className="route-job-price">${job.price || 0}</span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={applying}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleApply}
            disabled={applying || totalRoutes === 0 || totalJobs === 0}
          >
            {applying ? 'Applying...' : `Apply ${totalRoutes} route${totalRoutes !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AutoGroupModal;
